import { useEffect, useState } from "react";
import {
  adminService,
  type InactivityNudgeConfig,
  type InactivityNudgePreview,
} from "../../features/admin/service";

/**
 * Admin panel for the inactive-user WhatsApp nudge, written for a
 * NON-TECHNICAL admin: the mode is shown in plain words, every Preview row has
 * a plain-English status sentence, and a always-visible legend explains every
 * status and skip reason. Nothing here sends a message — Preview is a dry run.
 */

const STATUS_COLOR: Record<string, string> = {
  would_send: "bg-emerald-900/50 text-emerald-200 border-emerald-700",
  eligible: "bg-emerald-900/50 text-emerald-200 border-emerald-700",
  holdout: "bg-sky-900/50 text-sky-200 border-sky-700",
  deferred: "bg-amber-900/50 text-amber-200 border-amber-700",
  suppressed: "bg-zinc-800 text-zinc-300 border-zinc-600",
  exited: "bg-zinc-800 text-zinc-400 border-zinc-700",
  sent: "bg-emerald-900/50 text-emerald-200 border-emerald-700",
  unknown: "bg-orange-900/50 text-orange-200 border-orange-700",
  failed: "bg-red-900/50 text-red-200 border-red-700",
  claimed: "bg-indigo-900/50 text-indigo-200 border-indigo-700",
};

function Pill({ status, label }: { status: string; label?: string }) {
  const cls = STATUS_COLOR[status] || "bg-zinc-800 text-zinc-300 border-zinc-600";
  return (
    <span className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label || status}
    </span>
  );
}

export default function InactivityNudgePanel() {
  const [config, setConfig] = useState<InactivityNudgeConfig | null>(null);
  const [preview, setPreview] = useState<InactivityNudgePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string>("");
  const [fStatus, setFStatus] = useState<string>("all");
  const [fGroup, setFGroup] = useState<string>("all");
  const [fSearch, setFSearch] = useState<string>("");

  useEffect(() => {
    adminService.getInactivityNudgeConfig().then(setConfig).catch(() => setMsg("Could not load settings."));
  }, []);

  const patch = (p: Partial<InactivityNudgeConfig>) =>
    setConfig((c) => (c ? { ...c, ...p } : c));

  const save = async () => {
    if (!config) return;
    setSaving(true);
    setMsg("");
    try {
      // Only send the fields this form actually edits. Do NOT send templates /
      // fcm_templates / test_mode_recipients — those are managed elsewhere and a
      // stale form value would otherwise overwrite them on save.
      const patch = {
        mode: config.mode,
        enabled: config.enabled,
        run_interval_hours: config.run_interval_hours,
        inactivity_hours: config.inactivity_hours,
        max_age_days: config.max_age_days,
        holdout_pct: config.holdout_pct,
        per_run_cap: config.per_run_cap,
        daily_cap: config.daily_cap,
        send_window: config.send_window,
      };
      const saved = await adminService.updateInactivityNudgeConfig(patch);
      setConfig(saved);
      setMsg("Settings saved.");
    } catch {
      setMsg("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setLoading(true);
    setMsg("");
    try {
      setPreview(await adminService.getInactivityNudgePreview(500));
    } catch {
      setMsg("Preview failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!config) {
    return <div className="text-zinc-400">{msg || "Loading…"}</div>;
  }

  const s = preview?.summary;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">WhatsApp Nudges — Inactive users</h2>
        <p className="text-sm text-zinc-400">
          Finds people who signed up but never did anything real, and (when live) sends them one
          friendly WhatsApp. Start in <strong>SHADOW</strong> and use Preview — nothing is sent until you
          deliberately switch to LIVE.
        </p>
      </div>

      {/* Mode banner in plain words */}
      <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-3 text-sm text-white">
        <span className="font-semibold">Current mode: </span>
        {config.mode === "live" && config.enabled
          ? "LIVE — real WhatsApp messages are being sent."
          : config.mode === "dry_run"
          ? "DRY RUN — nothing is being sent."
          : "SHADOW — nothing is being sent. The job only works out who WOULD be messaged."}
      </div>

      {/* Settings */}
      <div className="grid grid-cols-1 gap-4 rounded-lg border border-zinc-700 bg-zinc-900 p-4 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm text-zinc-300">
          Mode
          <select
            className="mt-1 w-full rounded bg-zinc-800 p-2 text-white"
            value={config.mode}
            onChange={(e) => patch({ mode: e.target.value as InactivityNudgeConfig["mode"] })}
          >
            <option value="shadow">SHADOW (compute only)</option>
            <option value="dry_run">DRY RUN (full logic, no send)</option>
            <option value="live">LIVE (really send)</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={config.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />
          Feature ON (must also be LIVE to send)
        </label>
        <label className="text-sm text-zinc-300">
          Wait before nudging (hours)
          <input type="number" className="mt-1 w-full rounded bg-zinc-800 p-2 text-white"
            value={config.inactivity_hours}
            onChange={(e) => patch({ inactivity_hours: Number(e.target.value) })} />
        </label>
        <label className="text-sm text-zinc-300">
          How often the job runs (hours)
          <input type="number" className="mt-1 w-full rounded bg-zinc-800 p-2 text-white"
            value={config.run_interval_hours}
            onChange={(e) => patch({ run_interval_hours: Number(e.target.value) })} />
        </label>
        <label className="text-sm text-zinc-300">
          Don't nudge accounts older than (days)
          <input type="number" className="mt-1 w-full rounded bg-zinc-800 p-2 text-white"
            value={config.max_age_days}
            onChange={(e) => patch({ max_age_days: Number(e.target.value) })} />
        </label>
        <label className="text-sm text-zinc-300">
          Comparison group held back (%)
          <input type="number" className="mt-1 w-full rounded bg-zinc-800 p-2 text-white"
            value={config.holdout_pct}
            onChange={(e) => patch({ holdout_pct: Number(e.target.value) })} />
          <span className="mt-1 block text-xs text-zinc-500">
            We hold back 1 in 5 (20%) on purpose and never message them, so later we can compare
            "people we nudged" vs "people we left alone" and see if the nudge actually made a difference.
          </span>
        </label>
        <label className="text-sm text-zinc-300">
          Max per run
          <input type="number" className="mt-1 w-full rounded bg-zinc-800 p-2 text-white"
            value={config.per_run_cap}
            onChange={(e) => patch({ per_run_cap: Number(e.target.value) })} />
        </label>
        <label className="text-sm text-zinc-300">
          Max per day
          <input type="number" className="mt-1 w-full rounded bg-zinc-800 p-2 text-white"
            value={config.daily_cap}
            onChange={(e) => patch({ daily_cap: Number(e.target.value) })} />
        </label>
        <label className="text-sm text-zinc-300">
          Send window (IST)
          <div className="mt-1 flex items-center gap-1">
            <input className="w-full rounded bg-zinc-800 p-2 text-white" value={config.send_window?.start || ""}
              onChange={(e) => patch({ send_window: { ...config.send_window, start: e.target.value } })} />
            <span className="text-zinc-500">to</span>
            <input className="w-full rounded bg-zinc-800 p-2 text-white" value={config.send_window?.end || ""}
              onChange={(e) => patch({ send_window: { ...config.send_window, end: e.target.value } })} />
          </div>
        </label>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
          {saving ? "Saving…" : "Save settings"}
        </button>
        <button onClick={runPreview} disabled={loading}
          className="rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50">
          {loading ? "Working…" : "Preview (who would be messaged)"}
        </button>
        {msg && <span className="text-sm text-zinc-400">{msg}</span>}
      </div>

      {/* Preview results */}
      {preview && s && (
        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-3 text-sm text-white">
            {preview.mode_text}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="People checked" value={s.cohort_size} />
            <Stat label="Idle — not set up (A)" value={s.idle_A} />
            <Stat label="Idle — set up, quiet (B)" value={s.idle_B} />
            <Stat label="Would send" value={s.by_status.would_send || s.by_status.eligible || 0} />
            <Stat label="Held back" value={s.by_status.holdout || 0} />
            <Stat label="Already active" value={s.by_status.exited || 0} />
          </div>

          {/* Always-visible legend */}
          <details open className="rounded-lg border border-zinc-700 bg-zinc-900 p-3 text-sm">
            <summary className="cursor-pointer font-semibold text-white">What the labels mean</summary>
            <div className="mt-2 grid grid-cols-1 gap-1 text-zinc-300 sm:grid-cols-2">
              {Object.entries(preview.legend.status).map(([k, v]) => (
                <div key={k}><Pill status={k} /> <span className="text-zinc-400">{v}</span></div>
              ))}
            </div>
            <div className="mt-3 font-semibold text-white">Skip / wait reasons</div>
            <div className="mt-1 grid grid-cols-1 gap-1 text-zinc-400 sm:grid-cols-2">
              {Object.entries(preview.legend.reason).map(([k, v]) => (
                <div key={k}><span className="text-zinc-200">{k}</span> — {v}</div>
              ))}
            </div>
          </details>

          {/* Filters */}
          {(() => {
            const statuses = Array.from(new Set(preview.rows.map((r) => r.status))).sort();
            const groups = Array.from(new Set(preview.rows.map((r) => r.group || "—"))).sort();
            const filtered = preview.rows.filter((r) =>
              (fStatus === "all" || r.status === fStatus) &&
              (fGroup === "all" || (r.group || "—") === fGroup) &&
              (!fSearch || r.name.toLowerCase().includes(fSearch.toLowerCase())));
            return (
              <>
                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-zinc-700 bg-zinc-900 p-3">
                  <label className="text-xs text-zinc-400">What happens
                    <select className="mt-1 block rounded bg-zinc-800 p-2 text-sm text-white"
                      value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                      <option value="all">All statuses</option>
                      {statuses.map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </label>
                  <label className="text-xs text-zinc-400">Group
                    <select className="mt-1 block rounded bg-zinc-800 p-2 text-sm text-white"
                      value={fGroup} onChange={(e) => setFGroup(e.target.value)}>
                      <option value="all">All groups</option>
                      {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </label>
                  <label className="text-xs text-zinc-400 grow">Search name
                    <input className="mt-1 block w-full rounded bg-zinc-800 p-2 text-sm text-white"
                      placeholder="type a name…" value={fSearch}
                      onChange={(e) => setFSearch(e.target.value)} />
                  </label>
                  <button className="rounded bg-zinc-700 px-3 py-2 text-sm text-white hover:bg-zinc-600"
                    onClick={() => { setFStatus("all"); setFGroup("all"); setFSearch(""); }}>Clear</button>
                  <span className="text-xs text-zinc-400">{filtered.length} of {preview.rows.length} shown</span>
                </div>

                {/* Rows */}
                <div className="overflow-x-auto rounded-lg border border-zinc-700">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-zinc-800 text-zinc-300">
                      <tr>
                        <th className="px-3 py-2">Person</th>
                        <th className="px-3 py-2">Group</th>
                        <th className="px-3 py-2">What happens</th>
                        <th className="px-3 py-2">Exactly what gets sent</th>
                        <th className="px-3 py-2">Why</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {filtered.map((r) => (
                        <tr key={r.user_id} className="text-zinc-200">
                          <td className="px-3 py-2">{r.name}</td>
                          <td className="px-3 py-2" title={r.group_meaning}>{r.group || "—"}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col gap-1">
                              <Pill status={r.status} />
                              <span className="text-xs text-zinc-400">{r.status_sentence}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs" style={{ maxWidth: 360 }}>
                            {r.whatsapp ? (
                              <div className="mb-2">
                                <div className="font-medium text-emerald-300">WhatsApp</div>
                                <div className="text-zinc-500">template: {r.whatsapp.template}</div>
                                <div className="text-zinc-200">
                                  {r.whatsapp.message
                                    ? `"${r.whatsapp.message}"`
                                    : `(fills in: ${r.whatsapp.variables.map((v) => `"${v}"`).join(", ")} — body shows once template is approved)`}
                                </div>
                              </div>
                            ) : null}
                            {r.fcm && (r.fcm.title || r.fcm.body) ? (
                              <div>
                                <div className="font-medium text-sky-300">Push (FCM)</div>
                                <div className="text-zinc-200">{r.fcm.title}</div>
                                <div className="text-zinc-400">{r.fcm.body}</div>
                              </div>
                            ) : null}
                            {!r.whatsapp && !(r.fcm && (r.fcm.title || r.fcm.body)) ? (
                              <span className="text-zinc-500">—</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-400">
                            signed up {fmt(r.why.signed_up_at)} · clock from {fmt(r.why.anchor_at)} ·
                            {" "}last real action: {r.why.last_real_action || "none"} · consent: {r.why.consent}
                            {r.why.resume_text ? <> · <span className="text-zinc-300">message fills in: "{r.why.resume_text}"</span></> : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-3">
      <div className="text-2xl font-semibold text-white">{value}</div>
      <div className="text-xs text-zinc-400">{label}</div>
    </div>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!isNaN(d.getTime())) return d.toLocaleDateString();
  // Fallback: show the date portion of an unparseable ISO string rather than
  // "Invalid Date".
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : "—";
}
